import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;
// Base URL WITHOUT the /api suffix — use this for hrefs / <audio> src /
// browser download links whose path already starts with `/api/...`.
// Concatenating API_BASE with such a path produces `/api/api/...` → 404.
export const APP_ORIGIN = BACKEND_URL;

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Global 402 handler — when any AI endpoint reports insufficient credits or
// the legacy generation cap, dispatch a custom event so a top-level modal
// can take over. The original error still bubbles up to the caller for
// component-level loading state cleanup. We parse blob responses inline so
// the BYOK CapReachedModal fires even when callers use responseType:'blob'
// (e.g. ArtistDrops /generate which streams MP3 audio).
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    // FastAPI 422 validation errors return detail as an ARRAY of objects
    // ({type, loc, msg, input, url}) — rendering those in a toast crashes
    // React. Normalize to a readable string for every call site at once.
    if (Array.isArray(err?.response?.data?.detail)) {
      err.response.data.detail = err.response.data.detail
        .map((x) => (typeof x === "string" ? x : x?.msg || "Invalid input"))
        .join(" · ") || "Request failed — please check your input";
    }
    if (err?.response?.status === 402) {
      let d = err.response.data?.detail;
      if (!d && err.response.data instanceof Blob) {
        try {
          const txt = await err.response.data.text();
          const parsed = JSON.parse(txt);
          d = parsed?.detail || parsed;
          // Patch the error.response.data so downstream code can also read it
          err.response.data = parsed;
        } catch { /* leave d as undefined */ }
      }
      d = d || {};
      const eventName =
        d.error === "insufficient_credits"
          ? "bfa:credits-low"
          : d.error === "ai_cap_reached"
          ? "bfa:ai-cap-reached"
          : (d.error === "trial_expired" || d.error === "trial_grace")
          ? "bfa:trial-blocked"
          : d.error === "free_tier_exceeded"
          ? "bfa:ai-provider-capped"
          : null;
      if (eventName) {
        try {
          window.dispatchEvent(new CustomEvent(eventName, { detail: d }));
        } catch { /* no-op */ }
      }
    }
    return Promise.reject(err);
  }
);

export const fetchJSON = async (path, opts = {}) => {
  const res = await api.request({ url: path, ...opts });
  return res.data;
};
